import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Video, Award, Languages } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, MaskedLine, SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601052325717-f8140142e64e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHxfHxjYWxpZm9ybmlhJTIwY29hc3QlMjBmb2clMjByZWR3b29kJTIwZm9yZXN0JTIwc2VyZW5lJTIwbW9ybmluZ3xlbnwwfHx8fDE3ODg4MTI0MzN8MA&ixlib=rb-4.1.0&q=85";
const FOREST_IMG = "/images/couple-distance.jpg";
const PORTRAIT_IMG = "/images/breeza-quezada-headshot.jpg";

const helpItems = [
  { n: "01", text: "Improve communication without simply teaching couples to 'argue better'." },
  { n: "02", text: "Understand the emotional patterns that keep conflict going." },
  { n: "03", text: "Repair trust after betrayal and painful relationship events." },
  { n: "04", text: "Reconnect emotionally when a relationship has begun to feel distant." },
  { n: "05", text: "Navigate parenting, family, life transitions and other pressures that affect the relationship." },
];

const specialties = [
  {
    title: "Couples Therapy",
    copy: "The heart of my practice — structured, compassionate work for couples who love each other but can't seem to find their way back to each other.",
    to: "/couples-therapy",
    testid: "specialty-card-couples",
  },
  {
    title: "Infidelity & Rebuilding Trust",
    copy: "A focused, nonjudgmental process for couples navigating betrayal — whether you are repairing, understanding, or deciding what comes next.",
    to: "/infidelity-trust",
    testid: "specialty-card-infidelity",
  },
  {
    title: "Individual Therapy",
    copy: "Support for adults moving through anxiety, relationship difficulties, trauma, and life's hardest seasons.",
    to: "/individual-therapy",
    testid: "specialty-card-individual",
  },
];

const Home = () => {
  usePageMeta(
    "Couples Therapy in California | Holistic Healing Psychotherapy, Inc.",
    "Experienced, compassionate couples therapy for disconnection, conflict, infidelity and life transitions. California LCSW, nearly 20 years of clinical experience, secure telehealth."
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-cream-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="eyebrow mb-6"
              data-testid="hero-eyebrow"
            >
              Couples Therapy · California · Secure Telehealth
            </motion.p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink"
              data-testid="hero-headline"
            >
              <MaskedLine delay={0.25}>Couples Therapy for</MaskedLine>
              <MaskedLine delay={0.38}>Relationships</MaskedLine>
              <MaskedLine
                delay={0.51}
                className="font-['Great_Vibes',cursive] text-mauve-600 text-[58px] sm:text-[72px] lg:text-[88px] leading-[1.3] pb-[0.18em] [-webkit-text-stroke:1.25px_#CBAF70] [paint-order:stroke_fill]"
              >
                Worth <br className="sm:hidden" />Fighting For
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl"
              data-testid="hero-subcopy"
            >
              Experienced, compassionate therapy for couples navigating disconnection, conflict, infidelity,
              communication problems and major life transitions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-schedule-cta"
                className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm tracking-wide px-7 py-4 transition-all duration-300 hover:bg-forest-700 hover:shadow-[0_12px_32px_rgba(18,55,42,0.3)]"
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/couples-therapy"
                data-testid="hero-learn-cta"
                className="inline-flex items-center rounded-full border border-forest-800/25 text-forest-800 font-semibold text-sm tracking-wide px-7 py-4 transition-colors duration-300 hover:border-gold-500 hover:text-gold-600"
              >
                Learn About Couples Therapy
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-sage"
              data-testid="hero-trust-line"
            >
              <span className="flex items-center gap-2"><Award size={14} className="text-gold-500" /> California LCSW</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-gold-500" /> Nearly 20 Years of Clinical Experience</span>
              <span className="flex items-center gap-2"><Video size={14} className="text-gold-500" /> Secure Telehealth</span>
              <span className="flex items-center gap-2" data-testid="hero-spanish-badge"><Languages size={14} className="text-gold-500" /> Se Habla Español</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="editorial-frame"
            >
              <motion.div style={{ y: imgY }} className="relative z-10 overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="Morning fog settling through a quiet California forest — calm, grounded, restorative"
                  className="w-full h-[380px] sm:h-[480px] lg:h-[560px] object-cover"
                  data-testid="hero-image"
                />
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 z-20 bg-forest-900 text-cream-50 px-6 py-5 shadow-2xl max-w-[240px]"
              data-testid="hero-credential-card"
            >
              <p className="font-serif text-3xl text-gold-400">Nearly 20</p>
              <p className="text-xs uppercase tracking-[0.2em] text-cream-50/70 mt-1">Years of clinical practice in California</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* WHEN STUCK */}
      <section className="bg-cream-50 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <SectionHeading eyebrow="When Your Relationship Feels Stuck" title="You may love each other — and still feel miles apart." />
            <FadeIn delay={0.15}>
              <p className="text-base leading-relaxed text-ink/80 mt-2 max-w-xl" data-testid="stuck-copy">
                You may love each other and still find yourselves caught in the same arguments, carrying resentment,
                struggling to communicate, or wondering whether the relationship can be repaired. Therapy can provide
                a structured, supportive place to understand what is happening and begin doing something different.
              </p>
              <Link
                to="/infidelity-trust"
                data-testid="stuck-trust-link"
                className="inline-flex items-center gap-2 mt-7 text-sm font-semibold text-forest-700 hover:text-gold-600 transition-colors"
              >
                When trust has been broken <ArrowRight size={15} />
              </Link>
            </FadeIn>
          </div>
          <FadeIn delay={0.2} className="editorial-frame">
            <img
              src={FOREST_IMG}
              alt="A couple in a quiet bedroom, sitting in distance from each other"
              className="relative z-10 w-full h-[340px] sm:h-[440px] object-cover"
              data-testid="stuck-image"
            />
          </FadeIn>
        </div>
      </section>

      {/* HOW I HELP — numbered manifesto */}
      <section className="bg-forest-900 py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-gold-500/5 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <SectionHeading dark eyebrow="How I Help" title="Structured, compassionate work — aimed at real change." />
          <div className="mt-14 grid md:grid-cols-2 gap-x-16">
            {helpItems.map((item, i) => (
              <FadeIn key={item.n} delay={i * 0.08}>
                <div
                  className="group flex gap-6 py-7 border-t border-cream-50/10 hover:border-gold-500/40 transition-colors duration-500"
                  data-testid={`manifesto-item-${item.n}`}
                >
                  <span className="mt-3 w-3 h-3 rotate-45 bg-gold-500/70 group-hover:bg-gold-400 transition-colors duration-500 shrink-0" aria-hidden="true" />
                  <p className="text-base sm:text-lg text-cream-50/80 leading-relaxed">{item.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="bg-cream-100 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Areas of Focus" title="Specialized care for the seasons that test us most." />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {specialties.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.1}>
                <Link
                  to={s.to}
                  data-testid={s.testid}
                  className="group block h-full bg-cream-50 border border-gold-500/45 p-8 transition-all duration-500 hover:border-gold-500 hover:shadow-[0_20px_50px_rgba(18,55,42,0.12)] hover:-translate-y-1"
                >
                  <h3 className="font-serif text-2xl text-forest-800 mb-3">{s.title}</h3>
                  <p className="text-sm text-sage leading-relaxed">{s.copy}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-gold-600 group-hover:gap-3 transition-all duration-300">
                    Explore <ArrowRight size={14} />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WHY WORK WITH ME */}
      <section className="bg-cream-50 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <FadeIn className="editorial-frame order-2 lg:order-1">
            <img
              src={PORTRAIT_IMG}
              alt="Breeza Y. Quezada, LCSW — Holistic Healing Psychotherapy"
              className="relative z-10 w-full h-[380px] sm:h-[500px] object-contain bg-cream-200"
              data-testid="why-portrait-image"
            />
          </FadeIn>
          <div className="order-1 lg:order-2">
            <SectionHeading eyebrow="Why Work With Me" title="Clinical depth, delivered with warmth." />
            <FadeIn delay={0.15}>
              <p className="text-base leading-relaxed text-ink/80 mt-2" data-testid="why-copy">
                With nearly two decades in clinical social work and extensive behavioral-health experience, I bring
                clinical knowledge together with a warm, practical and nonjudgmental approach. I understand that
                relationships exist within real life — parenting, work, family responsibilities and unexpected
                challenges — and I bring that real-world perspective into every session.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-ink/80" data-testid="why-credentials-list">
                <li className="flex gap-3"><span className="text-gold-500">◆</span> California Licensed Clinical Social Worker (LCSW)</li>
                <li className="flex gap-3"><span className="text-gold-500">◆</span> Nearly 20 years of clinical experience</li>
                <li className="flex gap-3"><span className="text-gold-500">◆</span> Trauma-informed perspective</li>
                <li className="flex gap-3"><span className="text-gold-500">◆</span> Extensive behavioral-health experience</li>
              </ul>
              <Link
                to="/about-me"
                data-testid="why-about-link"
                className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-forest-700 hover:text-gold-600 transition-colors"
              >
                More about my story <ArrowRight size={15} />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* QUIET QUOTE BAND */}
      <section className="bg-mauve-500 py-14 lg:py-16" data-testid="home-quote-band">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <div data-testid="home-band-quote">
              <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-cream-50 leading-snug">
                "Love is a verb."
              </p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.3em] text-cream-50/80">— John Gottman</p>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Home;
